import { Communication } from '../../domain/entities/communication.entity';
import { CommunicationEvent, CommunicationEvents } from '../../domain/entities/communication-event.entity';
import { CommunicationEventDocument } from '../../domain/entities/communication-event-document.entity';
import { EntityConnectionAction } from '../../domain/value-objects/entity-connection-action.vo';
import { EntityCommunicationState } from '../../domain/value-objects/entity-communication-state.vo';
import { DocumentRole } from '../../domain/value-objects/document-role.vo';

// ── Communication list ────────────────────────────────────────────

export interface CommunicationApi {
  id: string;
  orderId?: string;
  contractId: number;
  qualityControlId: string | null;
  action: number;
  state: number;
  createdAt: string;
  finishedAt: string | null;
  isFinished: boolean;
  isSuccess: boolean | null;
  errors: string | null;
  requiresReview: boolean | null;
  reviewComments: string | null;
  isLinkedToQualityControl: boolean;
}

export interface CommunicationPagedApi {
  items: CommunicationApi[];
  pageNumber: number;
  pageSize: number;
  totalPages: number;
  totalItemsCount: number;
}

// ── Communication events ──────────────────────────────────────────

export interface CommunicationEventDocumentApi {
  id: string;
  name: string;
  fileName: string;
  size: number;
  extension: string;
  documentRole: string;
}

export interface CommunicationEventApi {
  id: string;
  occurredAt: string;
  isSuccess: boolean;
  response: string | null;
  documents: CommunicationEventDocumentApi[];
  primaryDocument: CommunicationEventDocumentApi | null;
}

export interface CommunicationEventsApi {
  communicationId: string;
  events: CommunicationEventApi[];
}

// ── Mapper ────────────────────────────────────────────────────────

export class CommunicationMapper {
  static toDomain(api: CommunicationApi): Communication {
    return {
      id: api.id,
      orderId: api.orderId,
      contractId: api.contractId,
      qualityControlId: api.qualityControlId,
      action: api.action as EntityConnectionAction,
      state: api.state as EntityCommunicationState,
      createdAt: new Date(api.createdAt),
      finishedAt: api.finishedAt ? new Date(api.finishedAt) : null,
      isFinished: api.isFinished,
      isSuccess: api.isSuccess,
      errors: api.errors ?? null,
      requiresReview: api.requiresReview,
      reviewComments: api.reviewComments ?? null,
      isLinkedToQualityControl: api.isLinkedToQualityControl,
    };
  }

  static toEventDocumentDomain(api: CommunicationEventDocumentApi): CommunicationEventDocument {
    return {
      id: api.id,
      name: api.name,
      fileName: api.fileName,
      size: api.size,
      extension: api.extension,
      documentRole: api.documentRole as DocumentRole,
    };
  }

  static toEventDomain(api: CommunicationEventApi): CommunicationEvent {
    return {
      id: api.id,
      occurredAt: new Date(api.occurredAt),
      isSuccess: api.isSuccess,
      response: api.response ?? null,
      documents: api.documents.map(CommunicationMapper.toEventDocumentDomain),
      primaryDocument: api.primaryDocument
        ? CommunicationMapper.toEventDocumentDomain(api.primaryDocument)
        : null,
    };
  }

  static toEventsDomain(api: CommunicationEventsApi): CommunicationEvents {
    return {
      communicationId: api.communicationId,
      events: api.events.map(CommunicationMapper.toEventDomain),
    };
  }
}
